import { FC, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ENV_CONFIG } from 'config/environment';
import { getIsSuper } from 'config/permissions';
import { TextField } from 'components/Forms/TextField';
import { DescriptionField } from 'components/Forms/DescriptionField';
import { useAppSelector } from 'helpers/hooks';
import { formatNumberAsCurrency, parseCurrency, required, transformToUrl } from 'helpers/utils';
import { getPagesState } from 'selectors/pages';
import { IPage } from 'store/pages/types';
import { UploadFileForm } from '../Forms/UploadFileForm';
import { ColorPickerForm } from '../Forms/ColorPickerForm';
import { RecipientDetailedForm } from '../Forms/RecipientDetailedForm';
import { validateWebsiteUrl } from '../utils';
import { getActiveRole } from 'selectors/admins';
import { RadioField } from 'components/Forms/RadioField';
import { displayFundraisingBarOptions, whiteLabelNavigationOptions } from '../constants';
import { SwitchToggleField } from 'components/Forms/SwitchToggleField';
import { AccordionButton } from './AccordionButton';
import { useFieldRestrictions } from 'hooks/useFieldRestrictions';
import { useFieldDirty } from 'hooks/useFormHelpers';
import { ConfirmationModal } from 'components/Modal/confirmationModal';
import { OfflineDonationForm } from '../Forms/OfflineDonationForm';

type Props = {
  index: number;
  itemVisible: number;
  handleClickAccordion: (index: number) => void;
  isPreviewVisible: boolean;
  formState: any;
  editPageSelected: IPage | undefined;
  isPageDuplicated: boolean;
};

export const RecommendationPageAccordion: FC<Props> = ({
  index,
  itemVisible,
  handleClickAccordion,
  isPreviewVisible,
  formState,
  editPageSelected,
  isPageDuplicated,
}) => {
  const pagesState = useAppSelector(getPagesState);
  const activeRole = useAppSelector(getActiveRole);
  const fieldRestrictions = useFieldRestrictions(!!editPageSelected);
  const [isQueryStringModalOpen, setIsQueryStringModalOpen] = useState(false);
  const [isQueryStringChangeConfirmed, setIsQueryStringChangeConfirmed] = useState(false);

  const queryStringDirty = useFieldDirty('query_string');

  useEffect(() => {
    formState.change('page_type', 'REC');
  }, []);

  const createFieldValidator = (validatorFn: any) => {
    return useMemo(() => {
      return (value: any) => {
        const stringValue = typeof value === 'string' ? value : value?.target?.value || '';
        return validatorFn(stringValue, pagesState, editPageSelected, isPageDuplicated);
      };
    }, [pagesState, editPageSelected, isPageDuplicated]);
  };

  const websiteUrlValidator = createFieldValidator(validateWebsiteUrl);
  const handleQueryStringBlur = () => {
    if (queryStringDirty && !isQueryStringChangeConfirmed && editPageSelected) {
      setIsQueryStringModalOpen(true);
    }
  };

  return (
    <div
      className={classNames('accordion-content', {
        'accordion-content--visible': itemVisible === index,
        'accordion-content--hidden': itemVisible !== index,
      })}
    >
      {index === 0 && (
        <div className="card">
          <ConfirmationModal
            isOpen={isQueryStringModalOpen}
            handleSave={() => {
              setIsQueryStringChangeConfirmed(true);
              setIsQueryStringModalOpen(false);
            }}
            handleCancel={() => {
              formState.change('query_string', formState.getState().initialValues?.query_string);
              setIsQueryStringModalOpen(false);
            }}
            warningMessage="Are you sure you want to change this page's URL? If this link has already been distributed to donors, we'd recommend to make a copy of this page to update the URL and pause this page with a redirect to the new URL."
          />
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Left-Side Panel</span>
            <div className="row">
              <div
                className={classNames('padding0', {
                  'col-md-5': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                {fieldRestrictions.canSee('page_name') && (
                  <TextField
                    label="Page Name *"
                    name="page_name"
                    type="text"
                    validate={required}
                    disabled={fieldRestrictions.isDisabled('page_name')}
                  />
                )}
              </div>
              <div
                className={classNames('padding0', {
                  'col-md-1': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              />
              <div
                className={classNames('padding0', {
                  'col-md-5': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <TextField label="Subtitle (optional)" name="subtitle" type="text" />
              </div>
            </div>
            <div className="row">
              <div
                className={classNames('padding0', {
                  'col-md-6': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <UploadFileForm type="tag" />
              </div>
              <div
                className={classNames('padding0', {
                  'col-md-6': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <ColorPickerForm
                  bgColorFieldName="page_background"
                  textColorFieldName="page_color_text"
                />
              </div>
            </div>
            <DescriptionField label="Page Description" name="description" />
            {fieldRestrictions.canSee('query_string') && (
              <>
                <TextField
                  label="Website URL *"
                  name="query_string"
                  type="text"
                  placeholder="example.com"
                  validate={websiteUrlValidator}
                  disabled={
                    (!getIsSuper(activeRole) && editPageSelected && !isPageDuplicated) ||
                    fieldRestrictions.isDisabled('query_string')
                  }
                  parse={value => transformToUrl(value).toLowerCase()}
                  format={value => value?.toLowerCase()}
                  customOnBlur={handleQueryStringBlur}
                />
                {formState.getState().values?.query_string && (
                  <div className="category form-category query-string">
                    {ENV_CONFIG().PARTNER_PAGE}
                    {formState.getState().values?.query_string}
                  </div>
                )}
              </>
            )}
            <AccordionButton
              onClick={() => handleClickAccordion(index + 1)}
              disabled={pagesState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 1 && (
        <div className="card">
          <div
            className={classNames('card-body card-body-page-detail', {
              'empty-recipients': !formState.getState().values.recipients_selected?.length,
            })}
          >
            <RecipientDetailedForm
              isPreviewVisible={isPreviewVisible}
              editPageSelected={editPageSelected}
            />
            <AccordionButton
              onClick={() => handleClickAccordion(getIsSuper(activeRole) ? index + 1 : index + 2)}
              disabled={pagesState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 2 && (
        <div className="card">
          <div className="card-body card-body-page-detail">
            <OfflineDonationForm
              isPreviewVisible={isPreviewVisible}
              editPageSelected={editPageSelected}
            />
            <AccordionButton
              onClick={() => handleClickAccordion(index + 1)}
              disabled={pagesState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 3 && (
        <div className="card">
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Custom Disclaimer Text</span>
            <DescriptionField
              label="Custom text that appears at the bottom of the Donation page, above Oath disclaimer."
              name="custom_disclaimer"
              placeholder="This is an optional field"
            />

            {fieldRestrictions.canSee('whitelabeled') && (
              <>
                <span className="oath-eyebrow inkBlue">White label navigation</span>
                <div className="col-md-6 padding0">
                  <RadioField
                    name="whitelabeled"
                    label="Show or hide Oath’s navigation bar on your Donation page:"
                    options={whiteLabelNavigationOptions}
                    disabled={fieldRestrictions.isDisabled('whitelabeled')}
                  />
                </div>
              </>
            )}
            {fieldRestrictions.canSee('is_oath_disclaimer_visible') && (
              <>
                <span className="oath-eyebrow inkBlue">Oath Disclaimer Visibility</span>
                <div className="col-md-12 padding0">
                  <SwitchToggleField
                    name="is_oath_disclaimer_visible"
                    label="If OFF, the Partner’s custom disclaimer should include ALL required disclaimer information."
                  />
                </div>
              </>
            )}
            {fieldRestrictions.canSee('active_page_status') && (
              <>
                <span className="oath-eyebrow inkBlue">Page Status</span>
                <div className="margin0 form-group">
                  <SwitchToggleField
                    name="active_page_status"
                    label="If OFF is selected, your page will be paused."
                  />
                </div>
              </>
            )}
            {!formState.getState().values?.active_page_status && (
              <>
                <TextField
                  label="Pause Description Copy*"
                  name="paused_description"
                  placeholder="100 characters recommended"
                  validate={required}
                />
                <div className="row">
                  <div
                    className={classNames({
                      'col-md-6 padding-left0': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  >
                    <TextField
                      label="Redirect Button Copy*"
                      name="paused_redirect_button"
                      placeholder="30 characters max"
                      maxLength={30}
                      validate={required}
                    />
                  </div>
                  <div
                    className={classNames({
                      'col-md-6 padding0': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  >
                    <TextField
                      label="Redirect Button URL (Where should your Paused page go to?)*"
                      name="paused_redirect_url"
                      placeholder="https/www.url.com"
                      validate={required}
                    />
                  </div>
                </div>
              </>
            )}
            <AccordionButton
              onClick={() => handleClickAccordion(index + 1)}
              disabled={pagesState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 4 && (
        <div className="card">
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Community card details</span>
            <div className="row">
              <div className="col-md-6 padding-left0">
                <TextField
                  label="Eyebrow Subtitle Text"
                  name="page_category"
                  placeholder="Appears above card title (35 characters max)"
                  maxLength={35}
                />
              </div>
              <div
                className={classNames('padding0', {
                  'col-md-6': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <UploadFileForm type="community" />
              </div>
            </div>
            <div className="row align-center">
              <div
                className={classNames({
                  'col-md-6 padding-left0': !isPreviewVisible,
                  'col-md-12 padding0': isPreviewVisible,
                })}
              >
                <TextField
                  label="Fundraising Goal*"
                  name="fundraising_goal"
                  type="text"
                  validate={required}
                  format={value => {
                    if (!value) return '';
                    const numValue = typeof value === 'string' ? parseFloat(value) : value;
                    return isNaN(numValue) ? value : formatNumberAsCurrency(numValue);
                  }}
                  parse={parseCurrency}
                />
              </div>
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-6': isPreviewVisible,
                })}
              >
                <RadioField
                  name="display_fundraising_bar"
                  label="Display fundraising bar?*"
                  options={displayFundraisingBarOptions}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-4 padding0">
                <TextField label="Bottom URL" name="learn_more_link" placeholder="Enter URL" />
              </div>
              <div className="col-md-2" />
              <div className="col-md-4 padding0">
                <TextField
                  label="Bottom URL Text (Default: Learn More)"
                  name="bottom_url_text"
                  placeholder="Learn More"
                />
              </div>
            </div>
            <AccordionButton
              type="submit"
              variant="submit"
              disabled={pagesState.isLoading}
              isSubmitting={formState.getState().submitting}
            >
              Publish
            </AccordionButton>
          </div>
        </div>
      )}
    </div>
  );
};
